import { AsyncSelect } from '@tempo-io/sui-select';
import { toast } from '@tempo-io/sui-sonner';
import debounce from 'lodash.debounce';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemTypeIcon } from '@/components/ui/item-type/itemTypeIcon';
import { ItemsApi } from '@/lib/api/common/items';
import type { ItemFiltersType } from '@/lib/api/types/itemFilters';
import type { Item } from '@/lib/api/types/items';
import type { ItemTypeMap } from '@/lib/api/types/itemType';

interface ItemPickerProps {
  value: { id: number; name: string; itemTypeId: number } | null;
  onChange: (item: { id: number; name: string; itemTypeId: number } | null) => void;
  itemTypesById: ItemTypeMap;
  disabled?: boolean;
  placeholder?: string;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  filter?: ItemFiltersType;
  autoFocus?: boolean;
  isClearable?: boolean;
  excludedItemIds?: number[];
}

const SEARCH_RESULTS_LIMIT = 50;
const DEBOUNCE_DELAY_MS = 300;
const MIN_SEARCH_LENGTH = 2;

function buildItemFilters(
  inputValue: string,
  additionalFilters?: ItemFiltersType,
): ItemFiltersType {
  const filters: ItemFiltersType = { ...additionalFilters };

  if (inputValue.trim() && inputValue.trim().length >= MIN_SEARCH_LENGTH) {
    filters.name = { value: inputValue.trim(), operator: 'startsWithIgnoreCase' };
  }

  return filters;
}

const EMPTY_ARRAY: number[] = [];

export const ItemPicker = ({
  value,
  onChange,
  itemTypesById,
  disabled,
  placeholder,
  onMenuOpen,
  onMenuClose,
  filter,
  autoFocus,
  isClearable = true,
  excludedItemIds = EMPTY_ARRAY,
}: ItemPickerProps) => {
  const { t } = useTranslation();

  const fetchItems = useMemo(
    () =>
      debounce((inputValue: string, callback: (options: Item[]) => void) => {
        if (inputValue.trim().length < MIN_SEARCH_LENGTH) {
          callback([]);
          return;
        }

        const fetchAsync = async () => {
          try {
            const filters = buildItemFilters(inputValue, filter);
            const response = await ItemsApi.getItems({
              filter: filters,
              order: 'name',
              limit: SEARCH_RESULTS_LIMIT,
              hierarchical: false,
            });

            const filteredData = response.data.filter((item) => !excludedItemIds.includes(item.id));

            callback([...filteredData]);
          } catch (_error) {
            toast.error(t(($) => $.adaptivePlanner.itemPanel.customFields.itemPicker.loadError));
            callback([]);
          }
        };
        fetchAsync();
      }, DEBOUNCE_DELAY_MS),
    [filter, t, excludedItemIds],
  );

  const formatOptionLabel = (option: Item) => {
    const itemType = itemTypesById[option.itemTypeId];
    return (
      <div className="flex items-center gap-2">
        {!!itemType && <ItemTypeIcon itemType={itemType} />}
        <span>{option.name}</span>
      </div>
    );
  };

  const pickerValue = value
    ? ({
        id: value.id,
        name: value.name,
        itemTypeId: value.itemTypeId,
      } as unknown as Item)
    : null;

  return (
    <AsyncSelect<Item>
      height={40}
      cacheOptions
      defaultOptions={false}
      loadOptions={fetchItems}
      value={pickerValue}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.id.toString()}
      formatOptionLabel={formatOptionLabel}
      onChange={(selected) => {
        if (selected) {
          onChange({
            id: selected.id,
            name: selected.name,
            itemTypeId: selected.itemTypeId,
          });
        } else {
          onChange(null);
        }
      }}
      placeholder={
        placeholder || t(($) => $.adaptivePlanner.itemPanel.customFields.itemPicker.placeholder)
      }
      isDisabled={disabled}
      isClearable={isClearable}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
      autoFocus={autoFocus}
      openMenuOnFocus={autoFocus && !value}
      noOptionsMessage={({ inputValue }) =>
        inputValue.trim().length < MIN_SEARCH_LENGTH
          ? t(($) => $.adaptivePlanner.itemPanel.customFields.itemPicker.minCharacters, {
              count: MIN_SEARCH_LENGTH,
            })
          : t(($) => $.adaptivePlanner.itemPanel.customFields.itemPicker.noResults)
      }
      data-testid="item-picker"
    />
  );
};
